import React, {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Dropzone, { DropzoneRef } from "react-dropzone";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { TextField } from "@material-ui/core";
import { ReactComponent as AttachFile } from "../../../assets/svg/attach-file-inside-circle.svg";
import FontIcon from "../../../components/FontIcon";
import { mutateErrors } from "../../../utils";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { useNotify } from "../../notify/useNotify";
import useMessageQueries from "../hooks/useMessageQueries";
import { IAttachedFile, ICreateMessageProps } from "../types";
import { AttachFileItem } from "./AttachFileItem";
import { useAuth } from "../../auth/hooks/useAuth";
import { UserRole } from "../../user/enums/UserRole";

const useStyle = makeStyles(() => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "end",
    columnGap: "8px",
    paddingRight: 24,
  },
  attachFileIcon: {
    minWidth: 46,
    borderRadius: "50%",
    transition: "box-shadow 0.2s ease-out",
    "&:hover": {
      boxShadow: "0px 0px 8px #858997",
      borderRadius: "50%",
      cursor: "pointer",
    },
  },
  attachFileIconDisableState: {
    opacity: 0.3,
    "&:hover": {
      boxShadow: "none",
      borderRadius: "50%",
      cursor: "default",
    },
  },
  attachFilesWithText: {
    border: "1px solid #DBDBDB",
    borderRadius: 16,
    padding: 12,
    flex: "auto",
  },
  disableState: {
    opacity: 0.3,
    background: "#f8f9fb",
    pointerEvents: "none",
  },
  scrollableContainer: {
    minHeight: 20,
    maxHeight: 170,
    wordBreak: "break-word",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  textArea: {
    "& .MuiInputBase-multiline": {
      padding: "2px 0 0 0",
    },
    "& .MuiInputBase-root": {
      fontSize: 14,
    },
  },
  attachFiles: {
    marginBottom: 8,
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  sendIcon: {
    marginBottom: 11,
    color: "#5351fb",
    transition: "color 0.2s ease-out",
    "&:hover": {
      color: "#4140c2",
      cursor: "pointer",
    },
  },
  sendIconDisableState: {
    color: "#eeeff3",
    "&:hover": {
      color: "#eeeff3",
      cursor: "default",
    },
  },
}));

const errorMessageBeautifier = (fileRejections, maxSize) => {
  fileRejections.map((file) => {
    const index = file.errors.findIndex(
      (error) => error.code === "file-too-large",
    );

    if (index >= 0 && maxSize) {
      const currentMessage = file.errors[index].message;

      file.errors[index].message = currentMessage.slice(0, 20) + maxSize + "MB";
    }
  });
};

export const CreateMessage = React.memo(
  ({ temp, onSendMessage }: ICreateMessageProps) => {
    const classes = useStyle();
    const dropzoneRef = createRef<DropzoneRef>();
    const textArea = useRef();
    const [attachedFiles, setAttachedFiles] = useState<IAttachedFile[]>([]);
    const [text, setText] = useState("");
    const [isSending, setIsSending] = useState(false);
    const { uploadAttachedFile } = useMessageQueries();
    const snackNotify = useNotify();
    const { user } = useAuth();
    const isAdmin = [UserRole.Owner, UserRole.Admin].includes(user.role);
    const isDisable = isAdmin || isSending;

    const maxFiles = 5;
    const maxSize = 28;

    useEffect(() => {
      setText(temp.text);
      setAttachedFiles(temp.attachments);
    }, [temp]);

    const changeText = (newText: string) => {
      temp.text = newText;
      setText(newText);
    };

    const changeAttachedFiles = (files: IAttachedFile[]) => {
      temp.attachments = files;
      setAttachedFiles(files);
    };

    const sendMessageHandler = async () => {
      if (isDisable) {
        return;
      }

      if (!text.trim().length && attachedFiles.length === 0) {
        changeText("");

        return;
      }

      setIsSending(true);

      try {
        await onSendMessage();
        changeText("");
        changeAttachedFiles([]);
      } catch (e) {
        const { message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
      } finally {
        setIsSending(false);
      }
    };

    const browse = () => {
      dropzoneRef.current?.open();
    };

    const addNewFiles = useCallback(
      async (files) => {
        const succeededFileNames: string[] = [];
        const succeededFile: IAttachedFile[] = attachedFiles;
        const errorMessages: string[] = [];

        for (const file of files) {
          try {
            let { data } = await uploadAttachedFile(file);
            succeededFile.push(data);
            succeededFileNames.push(file.name);
          } catch (e) {
            const { message } = mutateErrors(e);
            errorMessages.push(`${file.name} - ${message}`);
          }
        }

        if (errorMessages.length > 0) {
          snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
          return;
        }

        const successMessageEnding =
          succeededFileNames.length > 1
            ? "\nfiles have been uploaded."
            : " has been uploaded.";

        changeAttachedFiles(succeededFile);
        snackNotify(
          "Success",
          succeededFileNames.join("\n") + successMessageEnding,
          NotifyMode.SUCCESS,
        );
      },
      [attachedFiles],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    const removeFile = useCallback(
      (fileId) => {
        changeAttachedFiles(attachedFiles.filter((file) => file.id !== fileId));
      },
      [attachedFiles],
    );

    const onDropHandler = useCallback(
      async (acceptedFiles, fileRejections) => {
        if (attachedFiles.length + acceptedFiles.length > maxFiles) {
          snackNotify("Error", "Too many files!", NotifyMode.ERROR);

          return;
        }

        if (fileRejections.length) {
          errorMessageBeautifier(fileRejections, maxSize);
          const fileRejectionErrorsArray = fileRejections.map(
            (file) => file.errors[0].message,
          );
          const filteredRejectionErrors = Array.from(
            new Set(fileRejectionErrorsArray),
          ).join("\n");
          snackNotify("Error", filteredRejectionErrors, NotifyMode.ERROR);

          return;
        }

        if (acceptedFiles.length) {
          setIsSending(true);
          await addNewFiles!(acceptedFiles);
          setIsSending(false);
        }
      },
      [attachedFiles.length, maxFiles, snackNotify, maxSize, addNewFiles],
    );

    return (
      <div className={classes.root}>
        <AttachFile
          onClick={() => !isDisable && browse()}
          className={clsx(
            classes.attachFileIcon,
            isDisable && classes.attachFileIconDisableState,
          )}
        />
        <div
          /*@ts-ignore*/
          onClick={() => textArea.current.focus()}
          className={clsx(
            classes.attachFilesWithText,
            isDisable && classes.disableState,
          )}
        >
          <Dropzone
            maxFiles={maxFiles}
            maxSize={maxSize * 1048576} // maxSize * 1024 * 1024 bait
            accept={".pdf,.jpg,.jpeg,.png"}
            noClick={true}
            onDrop={onDropHandler}
            ref={dropzoneRef}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input
                  {...getInputProps({
                    multiple: true,
                    accept: ".pdf,.jpg,.jpeg,.png",
                  })}
                />
                {!!attachedFiles.length && (
                  <div className={classes.attachFiles}>
                    {attachedFiles.map((file) => (
                      <AttachFileItem
                        key={file.id}
                        id={file.id}
                        name={file.name}
                        size={file.size}
                        preview={file.preview}
                        removeFile={removeFile}
                      />
                    ))}
                  </div>
                )}
                <div className={classes.scrollableContainer}>
                  <div className={classes.textArea}>
                    <TextField
                      inputRef={textArea}
                      multiline
                      fullWidth
                      placeholder="Type a message..."
                      InputProps={{ disableUnderline: true }}
                      value={text}
                      onChange={(e) => !isDisable && changeText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.keyCode === 13 && !e.shiftKey) {
                          e.preventDefault();
                          sendMessageHandler();
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </Dropzone>
        </div>
        <FontIcon
          name="send"
          className={clsx(
            classes.sendIcon,
            isDisable && classes.sendIconDisableState,
          )}
          onClick={sendMessageHandler}
        />
      </div>
    );
  },
);
